import { Button, Dropdown } from 'antd';
import ButtonGroup from 'antd/lib/button/button-group';
import Barchart from 'components/molecules/Barchart';
import CopyToClipBoard from 'components/molecules/MicroIntractions/CopyTOClipBoard';
import DownloadExcel from 'components/molecules/MicroIntractions/DownloadExcel';
import MultiLineChart from 'components/molecules/MultiLineChart';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { calculateContainerHeight, formatNumWithKMBAlt, getConfig, scaleMaxValue } from 'utils/helperMethods';
import useKPIEvolutionData from './hooks/useKPIEvolutionData';
import { isEmpty } from 'lodash';
import { groupBarChartWidth } from 'components/organism/PVMAnalysis/PVMInfiniteImpactChartContainer';
import { useContainerWidth } from 'hooks/general/useContainerWidth';
import HorizontalInfiniteScroller from 'components/molecules/HorizontalInfiniteScroller';
import Loader from 'components/molecules/Loader';
import ErrorBox from 'components/molecules/ErrorBox/ErrorBox';
import { UserContext } from 'contexts/ConfigContext';
import CustomDropdownButton from 'components/atoms/CustomDropdownButton';
import DownSvg from 'assets/svg/Down';
import { FilterContextData } from 'contexts/FilterContext';
import { trackEventMixpanel } from 'utils/mixpanel';
import { PP_EVOLUTION_KPI_METRIC_CHANGED, PP_EVOLUTION_KPI_VIEW_TOGGLED } from 'utils/mixpanelEventNames';

const KPIEvolutionChart = () => {
    const { appConfig } = useContext(UserContext);
    const profitPoolConfig = getConfig('profitPool', appConfig);
    const { kpiEvolutionDropDownOptions } = profitPoolConfig;
    const currentRef = useRef<any>(null);
    const chartHeight = calculateContainerHeight(3);

    const containerWidth2 = useContainerWidth({
        containerId: 'kpi-evolution-chart',
    });
    const { allDimAndAttributeTypeList } = useContext(FilterContextData);

    const graphViewContainerWidth3 = Math.max(16 * groupBarChartWidth, containerWidth2);

    const [isLineChartActive, setIsLineChartActive] = useState(true);
    const [toLeft, setToLeft] = useState(0);
    const [selectedKPI, setSelectedKPI] = useState(kpiEvolutionDropDownOptions[0]);

    const lineChartColors = ['#D9D9D9', '#826FD0'];
    const barChartColors = ['#E8EAFA', '#4557D5'];

    const lineChartColorMapping = useMemo(() => {
        return {
            [selectedKPI?.label]: '#826FD0',
            [`${selectedKPI?.label} (YA)`]: '#D9D9D9',
        };
    }, [selectedKPI]);

    const {
        data: kpiEvolutionData,
        isPending,
        isError,
        isSuccess,
        fetchNextPage,
        min,
        max,
    } = useKPIEvolutionData(selectedKPI);

    const minBound = useMemo(() => {
        return min < 0 ? min : 0;
    }, [min]);

    // Prepare Excel export data
    const excelExportData = useMemo(() => {
        if (!kpiEvolutionData?.length) {
            return { headers: [], data: [] };
        }

        // Group data by period and pivot to have current year and year ago as columns
        const periodMap = new Map<string, { currentYear?: number; yearAgo?: number }>();

        kpiEvolutionData.forEach((item: any) => {
            const period = item.period || '';
            if (!periodMap.has(period)) {
                periodMap.set(period, {});
            }
            const periodData = periodMap.get(period)!;
            if (item.kpiName === `${selectedKPI?.label} (YA)`) {
                periodData.yearAgo = item.kpiValue;
            } else if (item.kpiName === selectedKPI?.label) {
                periodData.currentYear = item.kpiValue;
            }
        });

        const headers = ['Period', `${selectedKPI?.label} (YA)`, selectedKPI?.label || 'KPI Value'];

        const data = Array.from(periodMap.entries()).map(([period, values]) => [
            period,
            values.yearAgo ?? '',
            values.currentYear ?? '',
        ]);

        return { headers, data };
    }, [kpiEvolutionData, selectedKPI]);

    if (isPending || allDimAndAttributeTypeList?.loading) {
        return (
            <>
                <p className="text-lg font-semibold">{'KPI evolution'}</p>
                <div style={{ height: chartHeight, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <Loader />
                </div>
            </>
        );
    }

    if (isError) {
        return (
            <>
                <p className="text-lg font-semibold">{'KPI evolution'}</p>
                <div style={{ height: chartHeight, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <ErrorBox />
                </div>
            </>
        );
    }

    if (isSuccess && isEmpty(kpiEvolutionData)) {
        return (
            <>
                <p className="text-lg font-semibold">{'KPI evolution'}</p>
                <div style={{ height: chartHeight, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <ErrorBox msg={'No Data'} />
                </div>
            </>
        );
    }

    return (
        <>
            <div className="flex flex-row items-center justify-between">
                <p className="text-lg font-semibold">{'KPI evolution'}</p>
                <div className="flex flex-row gap-x-2 items-center">
                    <Dropdown
                        menu={{
                            items: kpiEvolutionDropDownOptions,
                            onClick: (info) => {
                                trackEventMixpanel(PP_EVOLUTION_KPI_METRIC_CHANGED, {
                                    kpi_name: kpiEvolutionDropDownOptions.find((option: any) => option.key === info.key)
                                        ?.label,
                                });
                                setSelectedKPI(
                                    kpiEvolutionDropDownOptions.find((option: any) => option.key === info.key),
                                );
                            },
                        }}
                        trigger={['click']}
                        placement="bottomRight"
                    >
                        <CustomDropdownButton type="default">
                            {selectedKPI?.label}
                            <div className="ml-2">
                                <DownSvg />
                            </div>
                        </CustomDropdownButton>
                    </Dropdown>
                    <ButtonGroup className="rounded-lg bg-[#F7F5FF]">
                        <Button
                            onClick={() => {
                                trackEventMixpanel(PP_EVOLUTION_KPI_VIEW_TOGGLED, {
                                    view_type: isLineChartActive ? 'bars' : 'line',
                                });
                                setIsLineChartActive(true);
                            }}
                            type={isLineChartActive ? 'primary' : 'text'}
                        >
                            {'Line'}
                        </Button>
                        <Button
                            type={isLineChartActive ? 'text' : 'primary'}
                            onClick={() => {
                                trackEventMixpanel(PP_EVOLUTION_KPI_VIEW_TOGGLED, {
                                    view_type: isLineChartActive ? 'bars' : 'line',
                                });
                                setIsLineChartActive(false);
                            }}
                        >
                            {'Bars'}
                        </Button>
                    </ButtonGroup>
                    <div className="flex flex-row gap-x-2 items-center">
                        <CopyToClipBoard refOfComponent={currentRef} />
                        <DownloadExcel
                            title={'KPI evolution'}
                            excelData={excelExportData.data}
                            headers={excelExportData.headers}
                        />
                    </div>
                </div>
            </div>

            <div ref={currentRef}>
                {/* Legends */}

                <div className="items-center flex flex-row justify-between my-2 mb-4">
                    <>
                        <div className="text-sm">
                            <p>{'KPI value'}</p>
                        </div>
                        <div className="flex flex-row gap-2 text-xs">
                            <div
                                style={{ backgroundColor: isLineChartActive ? lineChartColors[0] : barChartColors[0] }}
                                className="w-3 h-2 self-center"
                            />
                            <p>{`${selectedKPI?.label} (YA)`}</p>
                            <div
                                style={{ backgroundColor: isLineChartActive ? lineChartColors[1] : barChartColors[1] }}
                                className="w-3 h-2 self-center"
                            />
                            <p>{selectedKPI?.label}</p>
                        </div>
                        <div />
                    </>
                </div>

                {/* Chart */}
                <div className="flex">
                    <Barchart
                        padding={[0, 100, 15, 0]}
                        barWidth={0}
                        xAxisConfig={false}
                        dataset={[]}
                        height={chartHeight}
                        loading={false}
                        chartContainer={`kpi-evolution-chart-y1-axis`}
                        fields={['brand_id', 'abs_profit', 'type']}
                        minBound={minBound}
                        maxBound={scaleMaxValue(20, max)}
                        width={70}
                        colorKey={'type'}
                        YGrid={false}
                        yAxisConfig={true}
                        yAxisLabelFormatter={(val: any) => {
                            return `${selectedKPI?.unitLabel}${val}`;
                        }}
                    />
                    <HorizontalInfiniteScroller
                        id={`kpi-evolution-chart-container`}
                        hasMore={true}
                        scrollThreshold={0.9}
                        toScrollWidth={toLeft}
                        onNext={(width: number) => {
                            // fetchNextPage();
                        }}
                    >
                        <div id="kpi-evolution-chart-container" className="overflow-x-auto max-w-full pb-[60px]">
                            {isLineChartActive ? (
                                <MultiLineChart
                                    xAxisConfig
                                    lineStyle={{ opacity: 1, lineWidth: 1.2 }}
                                    showLegend
                                    padding={[5, 0, 5, 0]}
                                    dataset={kpiEvolutionData || []}
                                    height={chartHeight}
                                    loading={isPending}
                                    chartContainer={`kpi-evolution-chart-main`}
                                    fields={['period', 'kpiValue']}
                                    maxBound={scaleMaxValue(20, max)}
                                    minBound={minBound}
                                    width={graphViewContainerWidth3}
                                    xAxisLabelFormatter={(val: any) => {
                                        return val;
                                    }}
                                    colorKey={'kpiName'}
                                    colorMapping={lineChartColorMapping}
                                    dashedLineType={`${selectedKPI?.label} (YA)`}
                                    showLoader={false}
                                    tooltipFormatter={(xAxis, val) => {
                                        return {
                                            name: xAxis,
                                            value: `${selectedKPI?.unitLabel}${formatNumWithKMBAlt(val, 2, 2)}`,
                                        };
                                    }}
                                />
                            ) : (
                                <>
                                    <Barchart
                                        xAxisConfig
                                        showLegend={false}
                                        padding={[5, 0, 5, 0]}
                                        dataset={kpiEvolutionData || []}
                                        height={chartHeight}
                                        showLoader={false}
                                        loading={isPending}
                                        chartContainer={`kpi-evolution-chart-bars`}
                                        fields={['period', 'kpiValue', 'kpiName']}
                                        maxBound={scaleMaxValue(20, max)}
                                        minBound={minBound}
                                        width={graphViewContainerWidth3}
                                        xAxisLabelFormatter={(val: any) => {
                                            return val;
                                        }}
                                        colorKey={'kpiName'}
                                        enableGroupedChart
                                        groupedChartColor={barChartColors}
                                        tooltipFormatter={(xAxis, val) => {
                                            return {
                                                name: xAxis,
                                                value: `${selectedKPI?.unitLabel}${formatNumWithKMBAlt(val, 2, 2)}`,
                                            };
                                        }}
                                        intervalFormatter={(val) => {
                                            return `${selectedKPI?.unitLabel}${formatNumWithKMBAlt(val, 2, 2)}`;
                                        }}
                                        groupedChartMarginRatio={0.2}
                                    />
                                </>
                            )}
                        </div>
                    </HorizontalInfiniteScroller>
                </div>
            </div>
        </>
    );
};

export default KPIEvolutionChart;
